import { askToUnlock } from "../unlock.js";
import { isAdminActor } from "../../../shared/access.js";
import {
  badge, closeModal, confirmBox, del, esc, formError, formValues, formatDate, formatDateTime, get, openModal, patch, post, put, readFileAsDataUrl, toast,
} from "../lib.js";
import { icon } from "../icons.js";
import { navigate, onAction, onChange, onForm, refreshSession, rerender, route, state } from "../state.js";
import { loadOrgData, orgDataPanelsHtml } from "./orgdata.js";
import { PLM_NAV, backBar, errorPanel, loadingPanel, pageFor, renderShell, setContent } from "../shell.js";
import { ACCESS_LEVELS, ACTION_DEFS, COLUMN_DEFS, PAGE_DEFS, ROLES, canAction, hasPageAccess, roleTemplate, type Permissions, type Role } from "../../../shared/permissions.js";
import { CURRENCIES, FEATURE_DEFS, INDUSTRY_LABEL, type IndustryTemplate } from "../../../shared/licensing.js";
import { showInviteLink } from "./console.js";
import { whatYouMakeHtml } from "./setup.js";
import { LOGO_ACCEPT, LOGO_HINT, prepareLogo, showSidebarPreview, sidebarPreviewHtml, transparentBox } from "../logo.js";
import { SETTINGS_GROUPS, settingMatches, settingsGroupsFor, type SettingsItem } from "../../../shared/settingsmenu.js";
import { CRM_ROLES, CRM_ROLE_HINT, CRM_ROLE_LABEL } from "../../../shared/crm.js";
import { bothPortals, inCrmPortal } from "./crmaccess.js";

const actor = () => state.session.actor ?? null;
const isAdmin = () => isAdminActor(actor());
const support = () => !!actor()?.supportAccess;
/** Two-factor status in the user lists: On / Needed at sign-in / Off (optional). */
export const twoFactorBadge = (status: string) => (status === "on" ? badge("On") : status === "required" ? '<span class="badge amber">Needed at sign-in</span>' : '<span class="muted">Off (optional)</span>');


// ---- Audit trail list (shared with the Admin Console) -------------------------------
function changeValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? formatDate(s) : s;
}
export function auditListHtml(rows: any[], empty: string, showOrg = false): string {
  if (!rows.length) return `<div class="empty">${esc(empty)}</div>`;
  return `<div class="activity-list">${rows.map((e) => `<div class="activity-item ${e.supportAccess ? "falcon-support-entry" : ""}">
  <div class="activity-user">${esc(e.actorName || "System")}</div>
  <div class="activity-action">${esc(e.action)}${e.entityLabel && !String(e.action).includes(e.entityLabel) ? ` <span class="muted">· ${esc(e.entityLabel)}</span>` : ""}${showOrg && e.orgName ? ` <span class="muted">· ${esc(e.orgName)}</span>` : ""}
  ${Array.isArray(e.changes) && e.changes.length ? `<details class="falcon-changes"><summary>${e.changes.length} change${e.changes.length === 1 ? "" : "s"}</summary><table><thead><tr><th>Field</th><th>From</th><th>To</th></tr></thead><tbody>${e.changes.map((c: any) => `<tr><td>${esc(c.field)}</td><td>${esc(changeValue(c.from))}</td><td>${esc(changeValue(c.to))}</td></tr>`).join("")}</tbody></table></details>` : ""}</div>
  <div class="activity-time">${formatDateTime(e.createdAt)}</div>
</div>`).join("")}</div>`;
}

// ---- PLM modules still to come (Stage 2) ------------------------------------------------------
const MOVED: Record<string, string> = { products: "/app/garments", fabric: "/app/fabrics", trims: "/app/trims", tasks: "/app/tasks", po: "/app/po", critical: "/app/critical", sampling: "/app/sampling", techpacks: "/app/techpacks", rd: "/app/rd", qc: "/app/qc", compliance: "/app/compliance", shipmentcontrol: "/app/shipments", landedcosting: "/app/landed", supplierperformance: "/app/performance" };
route("/app/m/:page", ({ params }) => {
  if (MOVED[params.page]) return navigate(MOVED[params.page], true);
  const item = PLM_NAV.flatMap((n) => n.children || [n]).find((n) => n.id === params.page);
  const label = item?.label || "Module";
  renderShell({ active: params.page, title: label, subtitle: "Moving to the new platform in Stage 2.", content: "" });
  setContent(!hasPageAccess(actor(), params.page)
    ? errorPanel("You don't have access to this page.")
    : `<div class="panel falcon-coming-soon"><span class="falcon-admin-tile-icon">${icon(params.page)}</span><h3>${esc(label)} moves across in Stage 2</h3>
<p>This screen keeps its current layout and features, gets the improvements from the review, and your existing data is imported. Until then, keep using FALCON PLM v2.0.41 for day-to-day work.</p>
<a class="primary" href="/app" data-link>Back to Dashboard</a></div>`);
});

// ---- Settings console ----------------------------------------------------------------------------
route("/app/settings", () => {
  const org = state.session.organisation!;
  const row = (it: SettingsItem) => {
    const text = it.href === "/app/settings/codes" && org.adminsEditSetup === false ? `${it.text} View only (set by Falcon).` : it.text;
    return `<a class="settings-row" href="${it.href}" data-link><span class="settings-console-icon">${icon(it.icon)}</span><span class="settings-row-text"><strong>${esc(it.title)}</strong><small>${esc(text)}</small></span><span class="settings-row-go" aria-hidden="true">›</span></a>`;
  };
  // The Falcon CRM portal (or Falcon CRM only): the company, people and data settings, and Falcon CRM (no Falcon PLM
  // settings). Someone with both portals finds Falcon CRM's settings in the CRM portal, not here in Falcon PLM.
  const crmPortal = inCrmPortal();
  const shown = settingsGroupsFor({ plm: !crmPortal, crm: crmPortal || (!!org.crmEnabled && !bothPortals()) });
  const groups = shown.map((g) => `<section class="settings-group" id="settings-group-${g.id}" data-group="${g.id}">
<header><h3>${esc(g.title)} <span class="settings-group-count">${g.items.length}</span></h3><p>${esc(g.note)}</p></header>
<div class="settings-group-rows">${g.items.map(row).join("")}</div></section>`).join("");
  const jumps = shown.map((g) => `<button type="button" class="settings-jump" data-action="settings-jump" data-group="${g.id}">${esc(g.title)}</button>`).join("");
  renderShell({ active: "settings", title: "Settings", subtitle: "Organisation settings, users and security.", content: "" });
  setContent(`<div class="settings-console">
<div class="settings-console-intro"><div><span>ORGANISATION SETTINGS</span><h3>${esc(org.name)}</h3><p>Choose the area you want to manage, or type in Find a setting.</p></div></div>
${lockBarHtml()}
<div class="settings-tools">
  <label class="settings-find"><span class="sr-only">Find a setting</span><svg class="settings-find-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg><input type="search" id="settings-find" placeholder="Find a setting (e.g. currency, logo, sizes)" autocomplete="off" /></label>
  <div class="settings-jumps" role="navigation" aria-label="Go to a group">${jumps}</div>
</div>
<div class="settings-groups" id="settings-groups">${groups}</div>
<div class="settings-none" id="settings-none" hidden>No setting matches that. Try another word, such as “email” or “colours”.</div>
</div>`);
});

/** "Find a setting": show only the settings whose name, description or extra words contain every typed word. */
function findSettings(query: string): void {
  const items = new Map(SETTINGS_GROUPS.flatMap((g) => g.items).map((it) => [it.href, it]));
  let shown = 0;
  document.querySelectorAll<HTMLElement>(".settings-group").forEach((g) => {
    let inGroup = 0;
    g.querySelectorAll<HTMLElement>(".settings-row").forEach((r) => {
      const it = items.get(r.getAttribute("href") || "");
      const hit = !it || settingMatches(it, query);
      r.hidden = !hit;
      if (hit) inGroup++;
    });
    g.hidden = !inGroup;
    const count = g.querySelector(".settings-group-count");
    if (count) count.textContent = String(inGroup);
    shown += inGroup;
  });
  const none = document.getElementById("settings-none");
  if (none) none.hidden = shown > 0;
}
document.addEventListener("input", (e) => { const t = e.target as HTMLInputElement; if (t?.id === "settings-find") findSettings(t.value); });
onAction("settings-jump", (el) => {
  const find = document.getElementById("settings-find") as HTMLInputElement | null;
  if (find?.value) { find.value = ""; findSettings(""); }
  document.getElementById(`settings-group-${el.dataset.group}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
});

/** Settings locked / unlocked (changing settings needs the password or two-factor code in the last 15 minutes). */
function lockBarHtml(): string {
  const until = state.session.settingsUnlockedUntil as string | null;
  const open = !!until && new Date(until).getTime() > Date.now();
  const at = open ? new Date(until!).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
  return `<div class="settings-lock-bar ${open ? "is-open" : ""}" id="settings-lock-bar" role="status">
  <span class="settings-lock-icon" aria-hidden="true">${open ? "🔓" : "🔒"}</span>
  <span>${open ? `<strong>Settings unlocked</strong> until ${esc(at)}. Changes can be saved.` : "<strong>Settings locked.</strong> You can look at everything; saving a change asks for your password or two-factor code, then Settings stay unlocked for 15 minutes."}</span>
  ${open ? '<button type="button" class="secondary small-btn" data-action="settings-lock">Lock now</button>' : '<button type="button" class="primary small-btn" data-action="settings-unlock">Unlock</button>'}
</div>`;
}
const redrawLockBar = () => { const b = document.getElementById("settings-lock-bar"); if (b) b.outerHTML = lockBarHtml(); };
onAction("settings-unlock", async () => { await askToUnlock(); redrawLockBar(); });
onAction("settings-lock", async () => {
  await post("/api/account/settings-lock", {});
  state.session.settingsUnlockedUntil = null;
  redrawLockBar();
  toast("Settings locked.");
});
document.addEventListener("falcon:settings-unlocked", redrawLockBar);

// ---- Organisation profile ---------------------------------------------------------------------------
route("/app/settings/profile", async () => {
  renderShell({ active: "settings", title: "Organisation Profile & Branding", subtitle: "Company details, logo and report identity.", content: loadingPanel() });
  try {
    const o = await get("/api/org/profile");
    const ro = isAdmin() ? "" : "readonly disabled";
    const v = (k: string) => esc(o[k] ?? "");
    const field = (name: string, label: string, type = "text") => `<div class="form-group"><label>${label}</label><input name="${name}" type="${type}" value="${v(name)}" ${ro}></div>`;
    setContent(`${backBar("/app/settings", "Back to Settings")}
<div class="panel"><div class="panel-head"><div><h3>Organisation Profile &amp; Branding</h3><span class="muted">${isAdmin() ? "Admins can edit these details. Changes are recorded in the Activity Log." : "Only Admins can change these details."}</span></div></div>
<form data-form="org-profile" class="form-grid organisation-profile-form" novalidate>
<div class="form-message" role="alert" hidden></div>
<div class="form-group full organisation-logo-editor"><label>Company Logo</label><div class="logo-preview-pair"><div class="organisation-logo-preview" id="organisation-logo-preview">${o.hasLogo ? `<img src="/api/org/logo?v=${encodeURIComponent(o.updatedAt)}" alt="Logo">` : '<span class="no-logo-message">No logo uploaded</span>'}</div><div id="profile-sidebar-preview">${sidebarPreviewHtml(o.hasLogo ? `/api/org/logo?v=${encodeURIComponent(o.updatedAt)}` : null, o.name || "", !!o.logoTransparent)}</div></div>
${transparentBox(!!o.logoTransparent, "profile-logo-transparent", !isAdmin())}
${isAdmin() ? `<div class="settings-button-row"><label class="secondary falcon-file-btn">Upload Logo<input type="file" accept="${LOGO_ACCEPT}" hidden data-change="profile-logo-file"></label><button type="button" class="link-btn" data-action="profile-logo-remove">Remove</button></div><small>${esc(LOGO_HINT)}</small>` : ""}</div>
${field("name", "Organisation Name")}${field("legalName", "Legal Name")}${field("address1", "Address Line 1")}${field("address2", "Address Line 2")}
${field("city", "City")}${field("province", "Province / County")}${field("postcode", "Postcode")}${field("country", "Country")}
${field("telephone", "Telephone")}${field("email", "Company Email", "email")}${field("website", "Website")}${field("registrationNumber", "Registration Number")}
${field("taxNumber", "Tax / VAT Number")}
<div class="form-group"><label>Default Currency</label><select name="currency" ${ro}>${CURRENCIES.map((c) => `<option ${c === o.currency ? "selected" : ""}>${c}</option>`).join("")}</select></div>
${field("timezone", "Timezone")}
${isAdmin() ? `<div class="form-group full form-actions"><button type="submit" class="primary">Save Profile</button></div>` : ""}
</form></div>
${isAdmin() ? whatYouMakeHtml() : ""}
<div class="panel"><div class="panel-head"><div><h3>Licence</h3><span class="muted">Set by Falcon. Contact Falcon to change modules or seats.</span></div></div>
<div class="falcon-platform-kpis compact">
  <div><span>Falcon PLM</span><strong>${o.plmEnabled ? `${o.usage.plmUsed} / ${o.plmSeats} seats` : "Not subscribed"}</strong></div>
  <div><span>Falcon CRM</span><strong>${o.crmEnabled ? `${o.usage.crmUsed} / ${o.crmSeats} seats` : "Not subscribed"}</strong></div>
  <div><span>Subscription</span><strong>${esc(o.billingStatus)}</strong><small>${o.contractEnd ? `Renewal ${formatDate(o.contractEnd)}` : ""}</small></div>
  <div><span>Organisation Code</span><strong>${esc(o.code)}</strong></div>
</div></div>
${orgDataPanelsHtml()}`);
    void loadOrgData();
  } catch (err) {
    setContent(errorPanel((err as Error).message));
  }
});

onForm("org-profile", async (form) => {
  const v = formValues(form);
  try {
    await patch("/api/org/profile", {
      name: v.name, legalName: v.legalName, address1: v.address1, address2: v.address2, city: v.city, province: v.province, postcode: v.postcode, country: v.country,
      telephone: v.telephone, email: v.email, website: v.website, registrationNumber: v.registrationNumber, taxNumber: v.taxNumber, currency: v.currency, timezone: v.timezone,
    });
    await refreshSession();
    toast("Profile saved.");
    await rerender();
  } catch (err) {
    formError(form, err);
  }
});
let pendingLogo = "";
onChange("profile-logo-file", async (el) => {
  const input = el as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  try {
    pendingLogo = await prepareLogo(file);
  } catch (err) {
    return toast((err as Error).message, "error");
  }
  const transparent = !!state.session.organisation?.logoTransparent;
  openModal("New logo", `<div class="logo-upload-check"><p>This is how the logo will look at the top of the sidebar.</p><div id="logo-upload-preview"></div>
${transparentBox(transparent, "logo-upload-transparent")}
<div class="form-actions"><button type="button" class="secondary" data-action="close-modal">Cancel</button><button type="button" class="primary" data-action="profile-logo-use">Use This Logo</button></div></div>`);
  showSidebarPreview(document.getElementById("logo-upload-preview"), pendingLogo, state.session.organisation?.name || "", transparent);
});
onChange("logo-upload-transparent", (el) => {
  showSidebarPreview(document.getElementById("logo-upload-preview"), pendingLogo, state.session.organisation?.name || "", (el as HTMLInputElement).checked);
});
// Changed later without uploading again: saved at once, and the sidebar and preview follow.
onChange("profile-logo-transparent", async (el) => {
  const on = (el as HTMLInputElement).checked;
  const img = document.querySelector<HTMLImageElement>("#profile-sidebar-preview .sidebar-organisation-logo");
  showSidebarPreview(document.getElementById("profile-sidebar-preview"), img?.getAttribute("src") || null, state.session.organisation?.name || "", on);
  try {
    await patch("/api/org/profile", { logoTransparent: on });
    await refreshSession();
    const frame = document.querySelector<HTMLElement>(".sidebar .sidebar-organisation-logo-frame");
    if (frame) frame.dataset.look = on ? "clear" : "box";
    toast(on ? "Logo shown with a transparent background." : "Logo shown on a white box.");
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
onAction("profile-logo-use", async () => {
  try {
    const transparent = !!document.querySelector<HTMLInputElement>('input[data-change="logo-upload-transparent"]')?.checked;
    await put("/api/org/logo", { dataUrl: pendingLogo });
    if (transparent !== !!state.session.organisation?.logoTransparent) await patch("/api/org/profile", { logoTransparent: transparent });
    closeModal();
    await refreshSession();
    toast("Logo updated.");
    await rerender();
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
onAction("profile-logo-remove", async () => {
  if (!(await confirmBox("Remove the company logo?", "Remove", true))) return;
  await put("/api/org/logo", { dataUrl: null });
  await refreshSession();
  await rerender();
});

// ---- Features & template ---------------------------------------------------------------------------------
route("/app/settings/features", async () => {
  renderShell({ active: "settings", title: "Features & Industry Template", subtitle: "Switch features on or off for your organisation.", content: loadingPanel() });
  try {
    const o = await get("/api/org/profile");
    setContent(`${backBar("/app/settings", "Back to Settings")}
<div class="panel"><div class="panel-head"><div><h3>Features &amp; Industry Template</h3><span class="muted">Your organisation started from the ${esc(INDUSTRY_LABEL[o.industryTemplate as IndustryTemplate])} template. Nothing is deleted when you switch a feature off; it is just hidden.</span></div></div>
<form data-form="org-features" class="falcon-feature-form" novalidate>
<div class="form-message" role="alert" hidden></div>
<div class="falcon-feature-grid">${FEATURE_DEFS.map(([k, label]) => `<label class="check-row"><input type="checkbox" name="${k}" ${o.features[k] ? "checked" : ""} ${isAdmin() ? "" : "disabled"}><span>${esc(label)}</span></label>`).join("")}</div>
<div class="organisation-form-section-title"><strong>CRM–PLM Link</strong><span>When on, sales in Falcon CRM see Falcon PLM's garments under Products (view only: photos, colours, sizes, the price from the CRM price lists, when stock lands and the datasheet, never costs or suppliers), put them on deals, and send new product requests to the product team. Sales can never change PLM records or start production.</span></div>
<label class="check-row"><input type="checkbox" name="crmPlmLinkEnabled" ${o.crmPlmLinkEnabled ? "checked" : ""} ${isAdmin() && o.crmEnabled && o.plmEnabled ? "" : "disabled"}><span>Link CRM and PLM ${o.crmEnabled && o.plmEnabled ? "" : "(needs both modules)"}</span></label>
${isAdmin() ? `<div class="form-actions"><button type="submit" class="primary">Save Features</button></div>` : ""}
</form></div>`);
  } catch (err) {
    setContent(errorPanel((err as Error).message));
  }
});
onForm("org-features", async (form) => {
  const v = formValues(form);
  const features: Record<string, boolean> = {};
  for (const [k] of FEATURE_DEFS) features[k] = !!v[k];
  try {
    await patch("/api/org/features", { features, ...(form.querySelector<HTMLInputElement>('[name="crmPlmLinkEnabled"]')?.disabled ? {} : { crmPlmLinkEnabled: !!v.crmPlmLinkEnabled }) });
    await refreshSession();
    toast("Features saved.");
  } catch (err) {
    formError(form, err);
  }
});

// ---- User Database -----------------------------------------------------------------------------------
let usersCache: any = null;

route("/app/users", async () => {
  renderShell({ active: "settings", title: "User Database", subtitle: `Manage ${state.session.organisation?.name || ""} users, privileges and access permissions.`, content: loadingPanel() });
  try {
    const d = (usersCache = await get("/api/org/users"));
    const s = d.seats;
    const plmFull = s.plmEnabled && s.plmUsed >= s.plm;
    const crmFull = !s.crmEnabled || s.crmUsed >= s.crm;
    const allFull = (s.plmEnabled ? plmFull : true) && crmFull;
    const seatText = [s.plmEnabled ? `${s.plmUsed} / ${s.plm} PLM seats` : "", s.crmEnabled ? `${s.crmUsed} / ${s.crm} CRM seats` : ""].filter(Boolean).join(" · ");
    setContent(`<div class="settings-detail-return"><a class="secondary falcon-back-btn" href="/app/settings" data-link>← Back to Settings</a></div>
<div class="panel"><div class="panel-head"><div><h3>User Database</h3><span class="muted">${esc(seatText)} used. Admins are included. Deactivated users don't use a licence. Only Falcon can make someone an Admin or change an Admin.</span></div>
${d.canManageUsers ? `<div class="user-database-actions"><span class="licence-seat-count ${allFull ? "full" : ""}">${esc(seatText)}</span><button type="button" class="${allFull ? "secondary" : "primary"}" data-action="add-user" ${allFull ? 'data-full="1"' : ""}>${allFull ? "Licence Limit Reached" : "+ Add User"}</button></div>` : ""}</div>
${d.canManageUsers && allFull ? `<p class="form-message error">All ${s.plmEnabled ? s.plm : s.crm} licences in use — contact Falcon to add more.</p>` : ""}
${twoFactorLine(d)}
<div class="table-wrap user-db-wrap"><table class="user-permission-table user-db-table"><thead><tr><th>Code</th><th>Name</th><th>Email</th><th>Privilege</th><th>Modules</th><th>Status</th><th>Two-factor</th><th>Records</th><th>Access</th><th>Actions</th></tr></thead><tbody>
${d.users.map((u: any) => userRow(d, u)).join("")}
</tbody></table></div></div>`);
  } catch (err) {
    setContent(errorPanel((err as Error).message));
  }
});

const TWO_FACTOR_HELP = "Always required for Admins. When required for someone, they set it up at their next sign-in (authenticator app and recovery codes) and can't skip it. Anyone can turn it on for themselves in My Account.";
/** The two-factor rule above the table, on one line: the "everyone" tick (main Admin) or the rule, with a "?" that explains. */
function twoFactorLine(d: any): string {
  const help = `${TWO_FACTOR_HELP} ${d.canManageTwoFactor ? "Only you, as the main Admin, can change this." : "Only the organisation's main Admin can change this."}`;
  const q = `<span class="tf-help" tabindex="0" role="img" aria-label="${esc(help)}" title="${esc(help)}">?</span>`;
  return `<div class="falcon-two-factor-policy tf-line">${d.canManageTwoFactor
    ? `<label class="check-row tf-everyone"><input type="checkbox" data-change="two-factor-everyone" ${d.twoFactorPolicy === "all" ? "checked" : ""}><span>Require two-factor for everyone in this organisation</span></label>`
    : `<span class="tf-rule">Two-factor sign-in: <strong>${d.twoFactorPolicy === "all" ? "Required for everyone" : "Required for Admins and ticked users"}</strong></span>`}${q}</div>`;
}

/** "Require two-factor sign-in" for one person: a small "Required" tick beside the badge for the main Admin; the
 *  reason it is on (Admins, everyone) is in the tooltip. */
function twoFactorTick(d: any, u: any, self: boolean): string {
  const why = u.twoFactorRequiredBy === "admin" ? "Admins always need two-factor" : u.twoFactorRequiredBy === "everyone" ? "Required for everyone" : "";
  if (!d.canManageTwoFactor) return u.requireTwoFactor || why ? `<small class="muted tf-req-note" title="${esc(why || "Required for this user")}">Required</small>` : "";
  const locked = !!why || (self && d.twoFactorPolicy === "all");
  return `<label class="check-row tf-require" title="${esc(why || "Require two-factor sign-in for this user")}"><input type="checkbox" data-change="two-factor-user" data-id="${esc(u.membershipId)}" data-name="${esc(`${u.firstName} ${u.lastName}`)}" ${u.requireTwoFactor || why ? "checked" : ""} ${locked ? "disabled" : ""} aria-label="Require two-factor sign-in for ${esc(`${u.firstName} ${u.lastName}`)}"><span>Required</span></label>`;
}

/** The invitation email under "Invited", as plain text for the Status tooltip. */
function inviteTip(u: any): string {
  const e = u.lastInviteEmail;
  if (!u.pending || !e) return "";
  return e.ok ? `Invitation emailed ${formatDateTime(e.at)}` : `Invitation email failed (${formatDateTime(e.at)}): ${e.detail}`;
}

/** One user on one line: Code, Name, Email, Privilege, Modules, Status, Two-factor, Records, Access (the summary is the
 *  link) and Actions (Edit, and a More menu with every other action; Deactivate last, in red). */
function userRow(d: any, u: any): string {
  const self = u.membershipId === d.you;
  const falconOnly = u.role === "Admin" && !self && !support();
  const name = `${u.firstName} ${u.lastName}`;
  const tip = u.status === "active" ? inviteTip(u) : "";
  const failed = !!(u.pending && u.lastInviteEmail && !u.lastInviteEmail.ok && u.status === "active");
  const status = u.status !== "active" ? badge("Deactivated") : u.pending ? badge("Invited") : badge("Active");
  const statusCell = `<span class="user-status${failed ? " is-failed" : ""}" ${tip ? `title="${esc(tip)}" tabindex="0" aria-label="${esc(tip)}"` : ""}>${status}${failed ? '<span class="user-status-warn" aria-hidden="true">!</span>' : ""}</span>`;
  const tfTip = [u.twoFactorStatus === "on" ? "Two-factor is on." : u.twoFactorStatus === "required" ? "They set it up at their next sign-in." : "Off: they can turn it on in My Account.",
    u.twoFactorRequiredBy === "admin" ? "Admins always need two-factor." : u.twoFactorRequiredBy === "everyone" ? "Required for everyone in this organisation." : u.requireTwoFactor ? "Required for this user." : ""].filter(Boolean).join(" ");
  const access = `<a class="acc-summary-link" href="/app/users/${esc(u.membershipId)}/access" data-link title="${d.canManagePermissions && !self && !falconOnly ? "Change access" : "View access"}">${esc(u.accessSummary || (d.canManagePermissions && !self && !falconOnly ? "Access" : "View access"))}</a>`;
  const item = (label: string, attrs: string, cls = "") => `<button type="button" role="menuitem" class="user-more-item ${cls}" tabindex="-1" ${attrs}>${esc(label)}</button>`;
  const who = `data-id="${esc(u.membershipId)}" data-name="${esc(name)}" data-email="${esc(u.email)}"`;
  const more = [
    u.pending && u.status === "active" ? item("Resend invitation", `data-action="resend-invite" ${who}`) : "",
    u.pending && u.status === "active" && d.isAdmin ? item("Copy invitation link", `data-action="copy-invite-link" ${who}`) : "",
    d.canManageTwoFactor && u.twoFactor && !self ? item("Reset two-factor", `data-action="org-reset-2fa" ${who}`) : "",
    self ? "" : u.status === "active" ? item("Deactivate", `data-action="user-status" data-status="disabled" ${who}`, "is-danger")
      : item("Reactivate", `data-action="user-status" data-status="active" ${who}`) + (support() ? item("Delete permanently", `data-action="remove-user" ${who}`, "is-danger") : ""),
  ].filter(Boolean);
  const actions = !d.canManageUsers ? "" : falconOnly ? '<span class="muted">Managed by Falcon</span>'
    : `<div class="user-row-actions"><button type="button" class="link-btn" data-action="edit-user" data-id="${esc(u.membershipId)}">Edit</button>${more.length ? `<div class="user-more"><button type="button" class="secondary small-btn user-more-btn" data-action="user-more" aria-haspopup="menu" aria-expanded="false" aria-label="More actions for ${esc(name)}">More <span aria-hidden="true">▾</span></button><div class="user-more-menu" role="menu" aria-label="More actions for ${esc(name)}" hidden>${more.join("")}</div></div>` : ""}</div>`;
  return `<tr class="${u.status !== "active" ? "falcon-row-disabled" : ""}" data-user="${esc(u.membershipId)}">
<td data-label="Code"><strong>${esc(u.userCode)}</strong></td><td data-label="Name" class="user-name"><strong>${esc(name)}</strong></td><td data-label="Email" class="user-email" title="${esc(u.email)}">${esc(u.email)}</td>
<td data-label="Privilege">${badge(u.role, "gray")}</td><td data-label="Modules">${[u.plmAccess ? "PLM" : "", u.crmAccess ? `CRM${u.crmRole === "manager" ? " (Sales manager)" : ""}` : ""].filter(Boolean).join(" · ") || "—"}</td>
<td data-label="Status">${statusCell}</td>
<td data-label="Two-factor"><span class="user-tf" title="${esc(tfTip)}">${twoFactorBadge(u.twoFactorStatus)}${twoFactorTick(d, u, self)}</span></td>
<td data-label="Records">${esc(u.dataScope)}</td>
<td data-label="Access">${access}</td>
<td data-label="Actions" class="user-actions-cell">${actions}</td></tr>`;
}

// ---- The More menu on each user (opens over the table, never clipped by it) ------------------------------------------
const userMoreItems = (m: HTMLElement) => [...m.querySelectorAll<HTMLElement>(".user-more-item")];
function closeUserMore(focusButton = false) {
  for (const m of document.querySelectorAll<HTMLElement>(".user-more-menu:not([hidden])")) {
    m.hidden = true;
    const b = m.parentElement?.querySelector<HTMLElement>(".user-more-btn");
    b?.setAttribute("aria-expanded", "false");
    if (focusButton) b?.focus();
  }
}
onAction("user-more", (el, e) => {
  const m = el.parentElement?.querySelector<HTMLElement>(".user-more-menu");
  if (!m) return;
  const open = !m.hidden;
  closeUserMore();
  if (open) return;
  m.hidden = false;
  el.setAttribute("aria-expanded", "true");
  placeUserMore(m, el);
  if ((e as MouseEvent | undefined)?.detail === 0) userMoreItems(m)[0]?.focus();
});
/** Fixed to the window, under the button (or above it near the bottom), so the table never cuts it off. */
function placeUserMore(m: HTMLElement, btn: HTMLElement) {
  // The page text zoom (desktop) scales a fixed box's left / top, so window positions are divided by it.
  const z = (m as any).currentCSSZoom || parseFloat(getComputedStyle(document.getElementById("content") || document.body).zoom) || 1;
  const r = btn.getBoundingClientRect(), box = m.getBoundingClientRect(), w = Math.max(box.width, 180), h = box.height;
  if (r.bottom < 0 || r.top > window.innerHeight) { closeUserMore(); return; }
  const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
  const top = r.bottom + 4 + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 4) : r.bottom + 4;
  m.style.left = `${left / z}px`;
  m.style.top = `${top / z}px`;
}
/** Scrolling moves the open menu with its button. */
function followUserMore() {
  const m = document.querySelector<HTMLElement>(".user-more-menu:not([hidden])"), b = m?.parentElement?.querySelector<HTMLElement>(".user-more-btn");
  if (m && b) placeUserMore(m, b);
}
document.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  if (!document.querySelector(".user-more-menu:not([hidden])")) return;
  if (t.closest(".user-more-item")) { closeUserMore(); return; }
  if (!t.closest(".user-more")) closeUserMore();
}, true);
document.addEventListener("keydown", (e) => {
  const m = document.querySelector<HTMLElement>(".user-more-menu:not([hidden])");
  if (!m) return;
  const list = userMoreItems(m), i = list.indexOf(document.activeElement as HTMLElement);
  if (e.key === "Escape") { e.preventDefault(); closeUserMore(true); }
  else if (e.key === "ArrowDown") { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
  else if (e.key === "ArrowUp") { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
  else if (e.key === "Tab") closeUserMore();
}, true);
window.addEventListener("scroll", followUserMore, true);
window.addEventListener("resize", followUserMore);

function userForm(u: any | null): string {
  const s = usersCache.seats;
  const self = u && u.membershipId === usersCache.you;
  return `<form data-form="user" data-id="${esc(u?.membershipId || "")}" class="form-grid user-compact-form" novalidate>
<div class="form-message" role="alert" hidden></div>
<div class="form-group"><label>First Name</label><input name="firstName" required value="${esc(u?.firstName || "")}"></div>
<div class="form-group"><label>Last Name</label><input name="lastName" required value="${esc(u?.lastName || "")}"></div>
${u ? `<div class="form-group"><label>User Code</label><input value="${esc(u.userCode)}" readonly><small>Created from the user's initials and cannot be changed.</small></div>` : ""}
<div class="form-group"><label>Privilege</label><select name="role" ${self ? "disabled" : ""}>${ROLES.filter((r) => r !== "Admin" || support() || u?.role === "Admin").map((r) => `<option value="${r}" ${(u?.role || "Merchandiser") === r ? "selected" : ""}>${r}</option>`).join("")}</select>${self ? "<small>You can't change your own privilege.</small>" : `<small>The privilege is a label: what the user can open and do is set on their Access page.${support() ? "" : " Only Falcon can make someone an Admin."} A QC inspector sees only Ready for QC, their own inspections and Help.</small>`}</div>
<div class="form-group full"><label>Email Address</label><input name="email" type="email" required autocomplete="off" value="${esc(u?.email || "")}" ${u ? "readonly" : ""}><small>${u ? "The email identifies the person and can't be changed here." : `An invitation is emailed so they can choose their own password. The account connects them only to ${esc(state.session.organisation?.name || "")}.`}</small></div>
<div class="form-group full"><label>Modules</label><div class="falcon-inline-checks">
${s.plmEnabled ? `<label class="check-row"><input type="checkbox" name="plmAccess" ${u ? (u.plmAccess ? "checked" : "") : "checked"}><span>Falcon PLM</span></label>` : ""}
${s.crmEnabled ? `<label class="check-row"><input type="checkbox" name="crmAccess" ${u?.crmAccess ? "checked" : ""}><span>Falcon CRM</span></label>` : ""}</div><small>Each module uses one of the organisation's seats for that module. Someone with Falcon CRM only never sees Falcon PLM.</small></div>
${s.crmEnabled ? `<div class="form-group full"><label>CRM role</label><select name="crmRole">${CRM_ROLES.map((r) => `<option value="${r}" ${(u?.crmRole || "salesperson") === r ? "selected" : ""}>${CRM_ROLE_LABEL[r]}</option>`).join("")}</select><small>Used when Falcon CRM is ticked. ${esc(CRM_ROLE_HINT.salesperson)} A Sales manager ${esc(CRM_ROLE_HINT.manager.charAt(0).toLowerCase() + CRM_ROLE_HINT.manager.slice(1))}</small></div>` : ""}
${u && !self ? `<label class="check-row form-group full"><input type="checkbox" name="active" ${u.status === "active" ? "checked" : ""}><span>User account is active</span></label><small class="form-group full">Deactivated users can't sign in and don't use a licence. Their name stays on all records.</small>` : ""}
<div class="form-group full form-actions"><button type="button" class="secondary" data-action="close-modal">Cancel</button><button type="submit" class="primary">${u ? "Save User" : "Add User &amp; Send Invitation"}</button></div></form>`;
}

onAction("add-user", () => { openModal("Add User", userForm(null), { className: "user-modal-card" }); });
onAction("edit-user", (el) => {
  const u = usersCache.users.find((x: any) => x.membershipId === el.dataset.id);
  if (u) openModal(u.membershipId === usersCache.you ? "Your Account" : "Edit User", userForm(u), { className: "user-modal-card" });
});

onForm("user", async (form) => {
  const id = form.dataset.id;
  const v = formValues(form);
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  btn.disabled = true;
  try {
    const modules = { ...(v.plmAccess !== undefined ? { plmAccess: v.plmAccess } : { plmAccess: false }), ...(v.crmAccess !== undefined ? { crmAccess: v.crmAccess } : { crmAccess: false }), ...(v.crmRole ? { crmRole: v.crmRole } : {}) };
    if (id) {
      await patch(`/api/org/users/${id}`, { firstName: v.firstName, lastName: v.lastName, ...(v.role ? { role: v.role } : {}), ...modules, ...(v.active !== undefined ? { status: v.active ? "active" : "disabled" } : {}) });
      closeModal();
      toast("User saved.");
    } else {
      const r = await post("/api/org/users", { firstName: v.firstName, lastName: v.lastName, email: v.email, role: v.role, ...modules });
      closeModal();
      if (r.existingAccount) toast(`${r.message || "Added."} ${r.emailSent ? "A short notice has been emailed to them." : ""}`.trim());
      else if (r.inviteLink) showInviteLink(r.inviteLink, v.email, r.emailError && r.emailError.indexOf("isn't set up") < 0 ? "failed" : "notSetUp", r.emailError);
      else if (r.emailError) toast(`User ${r.userCode} added, but the invitation email failed: ${r.emailError}`, "error");
      else toast(r.invited ? `User ${r.userCode} added. An invitation has been emailed to ${v.email}.` : `User ${r.userCode} added. They already have a Falcon account, so they can sign in now.`);
    }
    await refreshSession();
    await rerender();
  } catch (err) {
    formError(form, err);
  } finally {
    btn.disabled = false;
  }
});

onAction("resend-invite", async (el) => {
  try {
    const r = await post(`/api/org/users/${el.dataset.id}/resend-invite`);
    if (r.inviteLink) showInviteLink(r.inviteLink, el.dataset.email || "this person", r.emailError && r.emailError.indexOf("isn't set up") < 0 ? "failed" : "notSetUp", r.emailError);
    else if (r.emailError) toast(`Invitation email failed: ${r.emailError}`, "error");
    else toast("Invitation email sent again (accepted by the mail server). If it doesn't arrive, ask them to check their junk folder, or use Copy invitation link.");
    await rerender();
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
onAction("user-status", async (el) => {
  const off = el.dataset.status === "disabled";
  if (off && !(await confirmBox(`Deactivate ${el.dataset.name}? They can't sign in from now on and their licence is freed. Their name stays on all records, and you can reactivate them later.`, "Deactivate", true))) return;
  try {
    await patch(`/api/org/users/${el.dataset.id}`, { status: el.dataset.status });
    toast(off ? `${el.dataset.name} deactivated.` : `${el.dataset.name} reactivated.`);
    await refreshSession();
    await rerender();
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
onAction("remove-user", async (el) => {
  if (!(await confirmBox(`Delete ${el.dataset.name} permanently? This can't be undone. Their past records stay, shown as "Deleted user (${el.dataset.name})".`, "Delete Permanently", true))) return;
  try {
    await del(`/api/org/users/${el.dataset.id}`);
    toast("User deleted.");
    await rerender();
  } catch (err) {
    toast((err as Error).message, "error");
  }
});

// ---- Access: the simple per-user list is on its own page (views/access.ts) ----------------------------------------------

// ---- Activity Log ----------------------------------------------------------------------------------------
let activityRows: any[] = [];
// The Activity Log: Reports → Activity Log (when the main Reports menu is switched on), and for Admins always under
// Settings → Activity Log (the same log, whatever the Reports switch says).
route("/app/activity", ({ query }) => activityView("/app/activity", query));
route("/app/settings/activity", ({ query }) => activityView("/app/settings/activity", query));
async function activityView(base: string, query: URLSearchParams) {
  const inSettings = base.startsWith("/app/settings");
  renderShell({ active: inSettings ? "settings" : "activity", title: "Activity Log", subtitle: "Complete saved history of user access and data activity.", content: loadingPanel() });
  const search = query.get("q") || "";
  try {
    activityRows = await get(`/api/org/activity?limit=100${search ? `&search=${encodeURIComponent(search)}` : ""}`);
    setContent(`${inSettings ? backBar("/app/settings", "Back to Settings") : ""}<div class="panel"><div class="panel-head"><div><h3>Activity Log</h3><span class="muted">Who changed what, when, and from what to what. Entries can't be edited or deleted.</span></div>
<form data-form="activity-search" data-base="${esc(base)}" class="falcon-inline-row" novalidate><input name="q" type="search" placeholder="Search name, action or email" value="${esc(search)}"><button type="submit" class="secondary">Search</button></form></div>
<div id="activity-holder">${auditListHtml(activityRows, search ? "Nothing matches that search." : "No activity yet.")}</div>
${activityRows.length === 100 ? `<div class="form-actions"><button type="button" class="secondary" data-action="activity-more" data-q="${esc(search)}">Load older entries</button></div>` : ""}
</div>`);
  } catch (err) {
    setContent(errorPanel((err as Error).message));
  }
}
onForm("activity-search", (form) => {
  const q = String(formValues(form).q || "").trim();
  return navigate(`${form.dataset.base || "/app/activity"}${q ? `?q=${encodeURIComponent(q)}` : ""}`);
});
onAction("activity-more", async (el) => {
  const before = activityRows[activityRows.length - 1]?.id;
  const more = await get<any[]>(`/api/org/activity?limit=100&before=${before}${el.dataset.q ? `&search=${encodeURIComponent(el.dataset.q)}` : ""}`);
  activityRows = activityRows.concat(more);
  document.getElementById("activity-holder")!.innerHTML = auditListHtml(activityRows, "");
  if (more.length < 100) el.remove();
});

void canAction;

onChange("two-factor-everyone", async (el) => {
  const box = el as HTMLInputElement;
  const policy = box.checked ? "all" : "admins";
  if (policy === "all" && !(await confirmBox("Require two-factor sign-in for everyone in this organisation? Everyone who hasn't set it up yet will be asked to at their next sign-in (with an authenticator app on their phone), and can't skip it.", "Require for everyone"))) {
    box.checked = false;
    return;
  }
  try {
    await put("/api/org/settings/two-factor", { policy });
    toast(policy === "all" ? "Two-factor sign-in is now required for everyone." : "Two-factor sign-in is now required for Admins and the users you tick.");
    await rerender();
  } catch (err) {
    box.checked = !box.checked;
    toast((err as Error).message, "error");
  }
});
onChange("two-factor-user", async (el) => {
  const box = el as HTMLInputElement;
  const required = box.checked;
  if (required && !(await confirmBox(`Require two-factor sign-in for ${el.dataset.name}? If they haven't set it up, they'll be asked to at their next sign-in and can't skip it.`, "Require"))) {
    box.checked = false;
    return;
  }
  try {
    await put(`/api/org/users/${el.dataset.id}/require-2fa`, { required });
    toast(required ? `Two-factor sign-in is now required for ${el.dataset.name}.` : `Two-factor sign-in is no longer required for ${el.dataset.name}.`);
    await rerender();
  } catch (err) {
    box.checked = !required;
    toast((err as Error).message, "error");
  }
});
onAction("org-reset-2fa", (el) => {
  openModal("Reset two-factor login", `
<form data-form="org-reset-2fa" data-id="${esc(el.dataset.id)}" class="form-grid" novalidate>
  <div class="form-message" role="alert" hidden></div>
  <div class="form-group full master-admin-note"><strong>${esc(el.dataset.name)}</strong><span>Use this when someone has lost their phone and their recovery codes. Check it's really them first. They'll be signed out and asked to set two-factor up again at their next sign-in. This is recorded in the Activity Log.</span></div>
  <div class="form-group full"><label>Reason</label><input name="reason" required maxlength="300" placeholder="For example: lost phone, confirmed by phone call"></div>
  <div class="form-group full form-actions"><button type="button" class="secondary" data-action="close-modal">Cancel</button><button type="submit" class="danger">Reset two-factor</button></div>
</form>`);
});
onForm("org-reset-2fa", async (form) => {
  try {
    await post(`/api/org/users/${form.dataset.id}/reset-2fa`, { reason: formValues(form).reason });
    closeModal();
    toast("Two-factor login reset.");
    await rerender();
  } catch (err) {
    formError(form, err);
  }
});

onAction("copy-invite-link", async (el) => {
  if (!(await confirmBox(`Create a new invitation link for ${el.dataset.email}? Links sent or copied earlier stop working.`, "Create Link"))) return;
  try {
    const r = await post(`/api/org/users/${el.dataset.id}/invite-link`);
    showInviteLink(r.inviteLink, el.dataset.email || "this person", "copy");
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
